<script setup lang="ts">
import NewsListingHeader from './header/NewsListingHeader.vue'
import NewsListingTags from './NewsListingTags.vue'
import NewsListingArticles from './NewsListingArticles.vue'
import NewsListingTagsSkeleton from './NewsListingTagsSkeleton.vue'
import Pagination from '~/components/global/Pagination.vue'
import Container from '~/components/layout/Container.vue'
import { usePaginationState } from '~/composables/pagination/usePaginationState'
import { useNewsListingData } from '~/composables/api/useNewsListingData'
import type { ArticlesResponse } from '~/types/api/Article'
import type { Tag, TagsResponse } from '~/types/api/Tag'
import { useNewsListingTags } from '~/composables/api/useNewsListingTags'
import PaginationSkeleton from '~/components/global/PaginationSkeleton.vue'

const route = useRoute()
const listingScrollHook = ref<HTMLDivElement | null>(null)
const paginationState = usePaginationState({
  type: 'articles',
  onPageChange: () => {
    if (!listingScrollHook.value) return

    scrollToElement({
      element: listingScrollHook.value,
    })
  },
})

const articlesResponse = useAsyncData<ArticlesResponse>(
  async () => {
    return await useNewsListingData({
      paginationState,
      route,
    })
  },
  {
    server: false,
    lazy: true,
  }
)

const hasArticlesData = computed(() => {
  return articlesResponse?.data?.value?.data?.length
})

watchEffect(() => {
  if (articlesResponse.pending.value) return

  paginationState.actions.setResponse(articlesResponse)
})

const tagsResponse = useAsyncData<TagsResponse>(
  async () => {
    const tags = await useNewsListingTags()

    return {
      data: tags.data.filter((tag: Tag) => tag.articles_count > 0),
      count: tags.count,
    }
  },
  {
    server: false,
    lazy: true,
  }
)

const activeTag = computed<Tag | undefined>(() =>
  tagsResponse?.data.value?.data?.find(
    (tag) => tag.id.toString() === route.params.tag_id
  )
)

// TODO: NTH - 301 redirect to correct slug if slug is incorrect
// maybe do it in middleware somehow?
// if (activeTag.value && activeTag.value.slug !== route.params.tag_slug) {
//   const router = useRouter()
//   router.replace(
//     useLocaleLink({
//       name: 'news-tag-tag_id',
//       params: {
//         tag_id: activeTag.value.id,
//         tag_slug: activeTag.value.slug,
//       },
//     })
//   )
// }

// redirect, if url slug is not matching the slug in fetched data
watch(activeTag, async (tag) => {
  if (tag && tag?.slug !== route.params.tag) {
    await navigateTo({
      path: useLocaleLink({
        name: 'news-tag-tag_id',
        params: {
          tag: tag.slug,
          id: tag.id,
        },
      })?.fullPath,
    })
  }
})
</script>

<template>
  <div class="c-news-listing">
    <NewsListingHeader :active-tag="activeTag" class="c-news-listing__header" />

    <div ref="listingScrollHook" />

    <div class="c-news-listing__tags">
      <NewsListingTagsSkeleton
        v-if="tagsResponse?.pending?.value && !tagsResponse?.data?.value"
      />

      <NewsListingTags
        v-else-if="tagsResponse.data.value?.data"
        :tags="tagsResponse.data.value?.data"
      />
    </div>

    <template v-if="articlesResponse.pending.value || hasArticlesData">
      <div class="c-news-listing__articles">
        <Container>
          <template v-if="paginationState.state.hasLastPagesData">
            <template
              v-for="(lastArticles, index) in paginationState.state
                .lastPagesData.value"
              :key="`last-articles-${index}`"
            >
              <NewsListingArticles
                :articles="lastArticles"
                :class="[
                  'sc-articles',
                  articlesResponse.pending.value &&
                    hasArticlesData &&
                    'is-pending',
                ]"
              />

              <hr />
            </template>
          </template>

          <NewsListingArticles
            :is-pending="articlesResponse.pending.value"
            :articles="articlesResponse?.data?.value?.data"
            :class="[
              'sc-articles',
              articlesResponse.pending.value && hasArticlesData && 'is-pending',
            ]"
          />
        </Container>
      </div>

      <Container class="c-news-listing__pagination">
        <PaginationSkeleton v-if="!hasArticlesData" />
        <Pagination v-else :pagination-state="paginationState" />
      </Container>
    </template>

    <template v-else>
      <Container>
        <IAlert color="info">
          {{ $t('pages.news.listing.empty') }}
        </IAlert>
        <br /><br />
      </Container>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-news-listing {
  * {
    overflow-anchor: none;
  }

  &__tags {
    margin-top: 40px;
    margin-bottom: 56px;
    transition: opacity var(--transition-duration)
      var(--transition-timing-function);

    &.is-pending {
      transition-delay: 0.15s;
      opacity: 0.3;
    }

    @include breakpoint-down('lg') {
      margin-top: 20px;
      margin-bottom: 20px;
    }
  }

  &__articles {
    margin-bottom: 80px;

    hr {
      margin: 60px 0;
      border-color: var(--color-solid-grey-2);
    }

    .sc-articles {
      transition: opacity var(--transition-duration)
        var(--transition-timing-function);

      &.is-pending {
        transition-delay: 0.5s;
        opacity: 0.3;
      }
    }

    @include breakpoint-down('lg') {
      margin-bottom: 50px;
    }
  }

  &__pagination {
    margin-bottom: 70px;

    @include breakpoint-down('lg') {
      margin-bottom: 40px;
    }
  }
}
</style>
